import {
  GlobalProduct,
  StockCatalogLocation,
  StockCatalogProduct,
  StockCatalogResponse,
  StockProductType,
} from 'types/models/StockReceipt';
import PaginationData from 'types/models/PaginationData';
import axiosInstance, { axiosMultipart } from './axios';
import joinURLParams from 'utils/joinUrlParams';

export const getVenueGlobalProducts = async (
  venueId: string,
  extraParams?: Record<string, any>,
): Promise<GlobalProduct[]> => {
  let url = `stock/global-products?venueId=${venueId}`;
  if (extraParams) {
    url = joinURLParams(extraParams, url);
  }
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });
    return parseGlobalProducts(data);
  } catch (error) {
    throw error;
  }
};

export const getCommerceGlobalProducts = async (
  commerceId: string,
  extraParams?: Record<string, any>,
): Promise<GlobalProduct[]> => {
  let url = `stock/global-products?commerceId=${commerceId}`;

  if (extraParams) {
    url = joinURLParams(extraParams, url);
  }
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });

    return parseGlobalProducts(data);
  } catch (error) {
    throw error;
  }
};

export const getGlobalProducts = async (
  page?: number,
  searchTerm?: string,
  venueIds?: string[],
  type?: StockProductType,
): Promise<PaginationData<GlobalProduct>> => {
  const url = joinURLParams({ page, searchTerm, venueIds, type }, 'stock/global-products');
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });
    return {
      currentPage: data.current_page,
      data: parseGlobalProducts(data.data),
      perPage: data.per_page,
      total: data.total,
    };
  } catch (error) {
    throw error;
  }
};

export const getVenueCatalog = async (
  venueId: string,
  warehouseIds?: string[],
  page?: number,
  searchTerm?: string,
  status?: string[],
): Promise<StockCatalogResponse> => {
  try {
    let url = `stock/catalog/${venueId}`;
    const params: Record<string, string | number | undefined> = { page, searchTerm };
    if (warehouseIds && warehouseIds.length > 0) {
      params.warehouseIds = warehouseIds.join(',');
    }
    if (status && status.length > 0) {
      params.status = status.join(',');
    }
    url = joinURLParams(params, url);

    const { data } = await axiosInstance({
      method: 'get',
      url,
    });

    return parseStockCatalogResponse(data);
  } catch (error) {
    throw error;
  }
};

export const exportVenueCatalog = async (
  venueId: string,
  warehouseIds?: string[],
  searchTerm?: string,
  status?: string[],
): Promise<Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `stock/catalog/${venueId}/export`,
      data: {
        warehouseIds: warehouseIds && warehouseIds.length > 0 ? warehouseIds.join(',') : null,
        searchTerm,
        status: status && status.length > 0 ? status.join(',') : null,
      },
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const parseStockCatalogResponse = (data: any): StockCatalogResponse => {
  const isPaginated = !Array.isArray(data?.data);
  const rawItems = isPaginated ? data?.data?.data ?? [] : data?.data ?? [];
  const products = parseStockCatalog(rawItems);

  return {
    totalValueInventoryCpp: Number(data?.total_value_inventory_cpp ?? 0),
    alertCount: Number(data?.alert_count ?? 0),
    criticalCount: Number(data?.critical_count ?? 0),
    minimumCount: Number(data?.minimum_count ?? 0),
    okCount: Number(data?.ok_count ?? 0),
    products,
    pagination: isPaginated
      ? {
          currentPage: Number(data?.data?.current_page ?? 1),
          perPage: Number(data?.data?.per_page ?? products.length),
          total: Number(data?.data?.total ?? products.length),
        }
      : {
          currentPage: 1,
          perPage: products.length,
          total: products.length,
        },
  };
};

const parseGlobalProducts = (data: unknown[]): GlobalProduct[] => {
  return data.map(parseGlobalProduct);
};

export const parseGlobalProduct = (product: any): GlobalProduct => {
  return {
    id: product.id,
    sku: product.sku,
    averageCost: product.average_cost,
    replacementCost: product.replacement_cost,
    name: product.name,
    type: product.type,
    venueId: product.venue_id,
    minimumStock: product.minimum_stock,
    safetyStock: product.safety_stock,
    unitMeasure: product.unit_measure ?? null,
    receptionUnitMeasure: product.reception_unit_measure ?? null,
    unitsPerReceptionUnit:
      product.units_per_reception_unit !== null && product.units_per_reception_unit !== undefined
        ? Number(product.units_per_reception_unit)
        : null,
    isUnitsLocked: Boolean(product.is_units_locked),
    image: product.image_path ?? null,
  };
};

export const getGlobalProduct = async (globalProductId: number): Promise<GlobalProduct> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `stock/global-products/${globalProductId}`,
    });
    return parseGlobalProduct(data);
  } catch (error) {
    throw error;
  }
};

export const createGlobalProduct = async (formData: FormData): Promise<GlobalProduct> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: 'stock/global-products',
      data: formData,
    });
    return parseGlobalProduct(data);
  } catch (error) {
    throw error;
  }
};

export const updateGlobalProduct = async (globalProductId: number, formData: FormData): Promise<GlobalProduct> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `stock/global-products/${globalProductId}?_method=put`,
      data: formData,
    });
    return parseGlobalProduct(data);
  } catch (error) {
    throw error;
  }
};

const parseStockCatalog = (products: any[]): StockCatalogProduct[] => {
  return products.map(parseStockCatalogProduct);
};

const parseStockCatalogProduct = (product: any): StockCatalogProduct => {
  return {
    globalProductId: product.global_product_id,

    sku: product.sku,
    name: product.name,
    type: product.type,
    averageCost: product.average_cost,
    unitMeasure: product.unit_measure ?? null,
    receptionUnitMeasure: product.reception_unit_measure ?? null,
    unitsPerReceptionUnit:
      product.units_per_reception_unit !== null && product.units_per_reception_unit !== undefined
        ? Number(product.units_per_reception_unit)
        : null,
    warehouses: parseStockCatalogLocations(product.warehouses),

    commerces: parseStockCatalogLocations(product.commerces),

    totalWarehouseStock: Number(product.total_warehouse_stock),

    totalCommerceStock: Number(product.total_commerce_stock),

    totalStock: Number(product.total_stock),

    minimumStock: product.minimum_stock ?? null,

    safetyStock: product.safety_stock ?? null,
  };
};

const parseStockCatalogLocations = (locations: any[]): StockCatalogLocation[] => {
  return locations.map((location) => ({
    id: location.id,
    name: location.name,
    stock: Number(location.stock),
  }));
};
